import { useState, useCallback, useEffect, useRef } from 'react';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridNoRecords, GridSortChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { Box, Button, ButtonGroup, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import PageContainerLayout from 'src/pages/PageContainerLayout';
import AdminApi from 'src/api/AdminApi';
import { MaxDiscountLogUserSummary } from 'src/types/maxDiscountLogUserSummary';
import { MaxDiscountLogItemSummary } from 'src/types/maxDiscountLogItemSummary';
import { userHasRole } from 'src/utils/userHelpers';

type DatePreset = '30' | '90' | '365';

const PRESET_LABELS: Record<DatePreset, string> = {
  '30': 'Last 30 Days',
  '90': 'Last 90 Days',
  '365': 'Last Year',
};

const getDateRange = (days: number): { fromDate: Date; toDate: Date } => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - days);
  return { fromDate, toDate };
};

const MaxDiscountSummary = () => {
  const isSalesManager = userHasRole(['Sales Manager']);
  const [activePreset, setActivePreset] = useState<DatePreset>('30');
  const [userSummary, setUserSummary] = useState<MaxDiscountLogUserSummary[]>([]);
  const [itemSummary, setItemSummary] = useState<MaxDiscountLogItemSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [userSort, setUserSort] = useState<SortDescriptor[]>([]);
  const [itemSort, setItemSort] = useState<SortDescriptor[]>([]);

  const userExportRef = useRef<ExcelExport | null>(null);
  const itemExportRef = useRef<ExcelExport | null>(null);

  const loadData = useCallback((days: number) => {
    const { fromDate, toDate } = getDateRange(days);
    setLoading(true);
    Promise.all([
      AdminApi.getMaxDiscountLogUserSummary(fromDate, toDate),
      AdminApi.getMaxDiscountLogItemSummary(fromDate, toDate),
    ])
      .then(([users, items]) => {
        setUserSummary(users);
        setItemSummary(items);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        console.error('Failed to fetch Max Discount summary data');
      });
  }, []);

  useEffect(() => {
    if (isSalesManager) {
      loadData(30);
    }
  }, [isSalesManager]);

  const handlePreset = (preset: DatePreset) => {
    setActivePreset(preset);
    loadData(Number(preset));
  };

  const handleUserSortChange = (e: GridSortChangeEvent) => setUserSort(e.sort);
  const handleItemSortChange = (e: GridSortChangeEvent) => setItemSort(e.sort);

  const handleUserExport = () => {
    if (userExportRef.current) {
      userExportRef.current.save(userSummary);
    }
  };

  const handleItemExport = () => {
    if (itemExportRef.current) {
      itemExportRef.current.save(itemSummary);
    }
  };

  if (!isSalesManager) {
    return (
      <PageContainerLayout pageHeaderTitle="Max Discount Summary">
        <p>You do not have permission to view this page. Sales Manager access required.</p>
      </PageContainerLayout>
    );
  }

  return (
    <div className="maxdiscountsummary-page">
      <PageContainerLayout pageHeaderTitle="Max Discount Summary">
        <Box sx={{ mb: 2 }}>
          <ButtonGroup variant="outlined" size="small">
            <Button
              variant={activePreset === '30' ? 'contained' : 'outlined'}
              onClick={() => handlePreset('30')}
            >
              Last 30 Days
            </Button>
            <Button
              variant={activePreset === '90' ? 'contained' : 'outlined'}
              onClick={() => handlePreset('90')}
            >
              Last 90 Days
            </Button>
            <Button
              variant={activePreset === '365' ? 'contained' : 'outlined'}
              onClick={() => handlePreset('365')}
            >
              Last Year
            </Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Card sx={{ flex: '0 0 60%', minWidth: 0 }}>
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                By User
              </Typography>
            </CardContent>
            <ExcelExport
              ref={userExportRef}
              fileName={`MaxDiscount_UserSummary_${PRESET_LABELS[activePreset].replace(/ /g, '_')}.xlsx`}
            >
              <ExcelExportColumn field="userName" title="User" />
              <ExcelExportColumn field="userSalesMgr" title="Sales Manager" />
              <ExcelExportColumn field="totalQuantityDiscounted" title="Total Qty" />
              <ExcelExportColumn field="distinctItemsDiscounted" title="Distinct Items" />
              <ExcelExportColumn field="totalDollarAmountDiscounted" title="Tot Discount" cellOptions={{ format: '$#,##0.00' }} />
              <ExcelExportColumn field="totalOrdersPlaced" title="Orders Placed" />
            </ExcelExport>
            <Grid
              data={orderBy(userSummary, userSort)}
              sortable
              sort={userSort}
              onSortChange={handleUserSortChange}
              scrollable="scrollable"
              style={{ maxHeight: '600px' }}
            >
              <GridToolbar>
                <Button size="small" variant="outlined" onClick={handleUserExport} disabled={loading || userSummary.length === 0}>
                  Export to Excel
                </Button>
              </GridToolbar>
              <GridColumn field="userName" title="User" width={200} />
              <GridColumn field="userSalesMgr" title="Sales Manager" width={200} />
              <GridColumn field="totalQuantityDiscounted" title="Total Qty" />
              <GridColumn field="distinctItemsDiscounted" title="Distinct Items" />
              <GridColumn field="totalDollarAmountDiscounted" title="Tot Discount" format="{0:c2}" />
              <GridColumn field="totalOrdersPlaced" title="Orders Placed" />
              <GridNoRecords>{loading ? <CircularProgress size={20} /> : 'No data for selected period.'}</GridNoRecords>
            </Grid>
          </Card>
          <Card sx={{ flex: '0 0 40%', minWidth: 0 }}>
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                By Item
              </Typography>
            </CardContent>
            <ExcelExport
              ref={itemExportRef}
              fileName={`MaxDiscount_ItemSummary_${PRESET_LABELS[activePreset].replace(/ /g, '_')}.xlsx`}
            >
              <ExcelExportColumn field="itemNumber" title="Item #" />
              <ExcelExportColumn field="pcatName" title="PCAT Name" />
              <ExcelExportColumn field="totalQuantity" title="Total Qty" />
              <ExcelExportColumn field="totalDollarDiscount" title="Total $ Discount" cellOptions={{ format: '$#,##0.00' }} />
              <ExcelExportColumn field="avgDiscountPct" title="Avg Disc%" cellOptions={{ format: '0.00%' }} />
            </ExcelExport>
            <Grid
              data={orderBy(itemSummary, itemSort)}
              sortable
              sort={itemSort}
              onSortChange={handleItemSortChange}
              scrollable="scrollable"
              style={{ maxHeight: '600px' }}
            >
              <GridToolbar>
                <Button size="small" variant="outlined" onClick={handleItemExport} disabled={loading || itemSummary.length === 0}>
                  Export to Excel
                </Button>
              </GridToolbar>
              <GridColumn field="itemNumber" title="Item #" width={150} />
              <GridColumn field="pcatName" title="PCAT Name" />
              <GridColumn field="totalQuantity" width={100} title="Total Qty" />
              <GridColumn field="totalDollarDiscount" title="Total $ Discount" format="{0:c2}" />
              <GridColumn field="avgDiscountPct" title="Avg Disc%" format="{0:p2}" />
              <GridNoRecords>{loading ? <CircularProgress size={20} /> : 'No data for selected period.'}</GridNoRecords>
            </Grid>
          </Card>
        </Box>
      </PageContainerLayout>
    </div>
  );
};

export default MaxDiscountSummary;
